import { NodeType } from './Types/TaskStatus';
/**
 * 行为树构建器
 *
 * 提供流式API构建行为树数据结构
 */
export class BehaviorTreeBuilder {
    constructor(treeName) {
        this.nodeStack = [];
        this.nodeIdCounter = 0;
        this.treeData = {
            id: `tree_${Date.now()}`,
            name: treeName,
            rootNodeId: '',
            nodes: new Map(),
            blackboardVariables: new Map()
        };
    }
    /**
     * 创建构建器
     */
    static create(treeName = 'BehaviorTree') {
        return new BehaviorTreeBuilder(treeName);
    }
    /**
     * 定义黑板变量
     */
    defineBlackboardVariable(key, initialValue) {
        if (!this.treeData.blackboardVariables) {
            this.treeData.blackboardVariables = new Map();
        }
        this.treeData.blackboardVariables.set(key, initialValue);
        return this;
    }
    /**
     * 添加序列节点
     */
    sequence(name) {
        return this.addCompositeNode('Sequence', name || 'Sequence');
    }
    /**
     * 添加选择器节点
     */
    selector(name) {
        return this.addCompositeNode('Selector', name || 'Selector');
    }
    /**
     * 添加并行节点
     */
    parallel(name, config) {
        return this.addCompositeNode('Parallel', name || 'Parallel', config);
    }
    /**
     * 添加并行选择器节点
     */
    parallelSelector(name, config) {
        return this.addCompositeNode('ParallelSelector', name || 'ParallelSelector', config);
    }
    /**
     * 添加随机序列节点
     */
    randomSequence(name) {
        return this.addCompositeNode('RandomSequence', name || 'RandomSequence');
    }
    /**
     * 添加随机选择器节点
     */
    randomSelector(name) {
        return this.addCompositeNode('RandomSelector', name || 'RandomSelector');
    }
    /**
     * 添加反转装饰器
     */
    inverter(name) {
        return this.addDecoratorNode('Inverter', name || 'Inverter');
    }
    /**
     * 添加重复装饰器
     */
    repeater(repeatCount, name) {
        return this.addDecoratorNode('Repeater', name || 'Repeater', { repeatCount });
    }
    /**
     * 添加总是成功装饰器
     */
    alwaysSucceed(name) {
        return this.addDecoratorNode('AlwaysSucceed', name || 'AlwaysSucceed');
    }
    /**
     * 添加总是失败装饰器
     */
    alwaysFail(name) {
        return this.addDecoratorNode('AlwaysFail', name || 'AlwaysFail');
    }
    /**
     * 添加直到成功装饰器
     */
    untilSuccess(name) {
        return this.addDecoratorNode('UntilSuccess', name || 'UntilSuccess');
    }
    /**
     * 添加直到失败装饰器
     */
    untilFail(name) {
        return this.addDecoratorNode('UntilFail', name || 'UntilFail');
    }
    /**
     * 添加条件装饰器
     */
    conditional(blackboardKey, expectedValue, operator, name) {
        return this.addDecoratorNode('Conditional', name || 'Conditional', {
            blackboardKey,
            expectedValue,
            operator: operator || 'equals'
        });
    }
    /**
     * 添加冷却装饰器
     */
    cooldown(cooldownTime, name) {
        return this.addDecoratorNode('Cooldown', name || 'Cooldown', { cooldownTime });
    }
    /**
     * 添加超时装饰器
     */
    timeout(timeout, name) {
        return this.addDecoratorNode('Timeout', name || 'Timeout', { timeout });
    }
    /**
     * 添加等待动作
     */
    wait(duration, name) {
        return this.addActionNode('Wait', name || 'Wait', { duration });
    }
    /**
     * 添加日志动作
     */
    log(message, name) {
        return this.addActionNode('Log', name || 'Log', { message });
    }
    /**
     * 添加设置黑板值动作
     */
    setBlackboardValue(key, value, name) {
        return this.addActionNode('SetBlackboardValue', name || 'SetBlackboardValue', { key, value });
    }
    /**
     * 添加修改黑板值动作
     */
    modifyBlackboardValue(key, operation, value, name) {
        return this.addActionNode('ModifyBlackboardValue', name || 'ModifyBlackboardValue', {
            key,
            operation,
            value
        });
    }
    /**
     * 添加执行动作
     */
    executeAction(actionName, name) {
        return this.addActionNode('ExecuteAction', name || 'ExecuteAction', { actionName });
    }
    /**
     * 添加黑板比较条件
     */
    blackboardCompare(key, compareValue, operator, name) {
        return this.addConditionNode('BlackboardCompare', name || 'BlackboardCompare', {
            key,
            compareValue,
            operator: operator || 'equals'
        });
    }
    /**
     * 添加黑板存在检查条件
     */
    blackboardExists(key, name) {
        return this.addConditionNode('BlackboardExists', name || 'BlackboardExists', { key });
    }
    /**
     * 添加随机概率条件
     */
    randomProbability(probability, name) {
        return this.addConditionNode('RandomProbability', name || 'RandomProbability', { probability });
    }
    /**
     * 添加执行条件
     */
    executeCondition(conditionName, name) {
        return this.addConditionNode('ExecuteCondition', name || 'ExecuteCondition', { conditionName });
    }
    /**
     * 结束当前节点，返回父节点
     */
    end() {
        if (this.nodeStack.length > 0) {
            this.nodeStack.pop();
        }
        return this;
    }
    /**
     * 构建行为树数据
     */
    build() {
        if (!this.treeData.rootNodeId) {
            throw new Error('No root node defined. Add at least one node to the tree.');
        }
        return this.treeData;
    }
    addCompositeNode(implementationType, name, config = {}) {
        const nodeId = this.generateNodeId();
        const node = {
            id: nodeId,
            name,
            nodeType: NodeType.Composite,
            implementationType,
            children: [],
            config
        };
        this.treeData.nodes.set(nodeId, node);
        if (!this.treeData.rootNodeId) {
            this.treeData.rootNodeId = nodeId;
        }
        if (this.nodeStack.length > 0) {
            const parentId = this.nodeStack[this.nodeStack.length - 1];
            const parentNode = this.treeData.nodes.get(parentId);
            if (parentNode && parentNode.children) {
                parentNode.children.push(nodeId);
            }
        }
        this.nodeStack.push(nodeId);
        return this;
    }
    addDecoratorNode(implementationType, name, config = {}) {
        const nodeId = this.generateNodeId();
        const node = {
            id: nodeId,
            name,
            nodeType: NodeType.Decorator,
            implementationType,
            children: [],
            config
        };
        this.treeData.nodes.set(nodeId, node);
        if (!this.treeData.rootNodeId) {
            this.treeData.rootNodeId = nodeId;
        }
        if (this.nodeStack.length > 0) {
            const parentId = this.nodeStack[this.nodeStack.length - 1];
            const parentNode = this.treeData.nodes.get(parentId);
            if (parentNode && parentNode.children) {
                parentNode.children.push(nodeId);
            }
        }
        this.nodeStack.push(nodeId);
        return this;
    }
    addActionNode(implementationType, name, config = {}) {
        const nodeId = this.generateNodeId();
        const node = {
            id: nodeId,
            name,
            nodeType: NodeType.Action,
            implementationType,
            config
        };
        this.treeData.nodes.set(nodeId, node);
        if (!this.treeData.rootNodeId) {
            this.treeData.rootNodeId = nodeId;
        }
        if (this.nodeStack.length > 0) {
            const parentId = this.nodeStack[this.nodeStack.length - 1];
            const parentNode = this.treeData.nodes.get(parentId);
            if (parentNode && parentNode.children) {
                parentNode.children.push(nodeId);
            }
        }
        return this;
    }
    addConditionNode(implementationType, name, config = {}) {
        const nodeId = this.generateNodeId();
        const node = {
            id: nodeId,
            name,
            nodeType: NodeType.Condition,
            implementationType,
            config
        };
        this.treeData.nodes.set(nodeId, node);
        if (!this.treeData.rootNodeId) {
            this.treeData.rootNodeId = nodeId;
        }
        if (this.nodeStack.length > 0) {
            const parentId = this.nodeStack[this.nodeStack.length - 1];
            const parentNode = this.treeData.nodes.get(parentId);
            if (parentNode && parentNode.children) {
                parentNode.children.push(nodeId);
            }
        }
        return this;
    }
    generateNodeId() {
        return `node_${this.nodeIdCounter++}`;
    }
}
//# sourceMappingURL=BehaviorTreeBuilder.js.map